import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { API_BASE_URL } from '../config/config';
import {
  FREE_COURSES_PLAYED_LIMIT,
  isCoursesPlayedCapped,
} from '../constants/MemberTypeConstants';
import {
  MemberBucketListClient,
  MemberClient,
  MemberCoursesPlayedClient,
  MemberHomeCourseClient,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';
import { showToast } from '../utils/showToast';
import { isSignedIn } from '../utils/tokenUtils';

// The Explore map's two ticks ("I have played this course" and "add to my bucket list") held as
// the member's real, saved state rather than as screen-only state, plus the member's home courses,
// which are read-only here: they are set on /member/home-courses, and Explore only needs to know
// which courses they are so its filter bar can show them.
//
// Reads come from the same `me` endpoints the member pages use. Writes go to the member-scoped
// PUT/DELETE routes (`/member/me/bucket-list/{courseId}` and `/member/me/courses-played/{courseId}`),
// which resolve the member from the token, so nothing here has to know or send a member id.
//
// Ids are lower-cased on the way in and out, per CLAUDE.md: GUID casing is not guaranteed to survive
// a round trip, and a pin click hands the id back through Mapbox's serialized feature properties
// rather than from the original object.

const bucketListClient = new MemberBucketListClient(
  API_BASE_URL,
  axiosInstance,
);
const coursesPlayedClient = new MemberCoursesPlayedClient(
  API_BASE_URL,
  axiosInstance,
);
const homeCourseClient = new MemberHomeCourseClient(
  API_BASE_URL,
  axiosInstance,
);
const memberClient = new MemberClient(API_BASE_URL, axiosInstance);

type CourseIdSet = Set<string>;

// The free tier is full. The backend refuses the 31st played course with a 403 whose message is
// written to be shown to the member as-is (MemberCoursesPlayedFunction.ToggleForCurrentMemberAsync).
// Deliberately not a 402: the axios interceptor answers a 402 by throwing the member out to
// /payment-required, and a member who ran out of free slots is not locked out of anything.
const FREE_LIMIT_STATUS = 403;

// Said the same way whether the browser worked it out first or the server refused. The server's
// own wording is used when there is one, so the two can never contradict each other.
export const FREE_LIMIT_MESSAGE = `A free membership holds ${FREE_COURSES_PLAYED_LIMIT} courses played. Upgrade your membership to add more.`;

export const freeLimitMessageFrom = (error: unknown): string | null => {
  const response = (error as AxiosError)?.response;
  if (response?.status !== FREE_LIMIT_STATUS) {
    return null;
  }

  const message = (response.data as { message?: string } | undefined)?.message;
  return message != null && message.trim().length > 0
    ? message
    : FREE_LIMIT_MESSAGE;
};

interface CourseIdRow {
  courseId?: string | undefined;
}

const toCourseIdSet = (rows: CourseIdRow[] | undefined): CourseIdSet =>
  new Set(
    (rows ?? [])
      .map((row) => row.courseId)
      .filter((id): id is string => id != null)
      .map((id) => id.toLowerCase()),
  );

const withId = (
  ids: CourseIdSet,
  id: string,
  present: boolean,
): CourseIdSet => {
  // A new Set every time — mutating the existing one and returning it is the same reference, so
  // React bails out of the render and the checkbox never moves.
  const next = new Set(ids);
  if (present) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
};

export interface MyCourseFlags {
  playedCourseIds: CourseIdSet;
  bucketListCourseIds: CourseIdSet;
  // Read-only. There is no tick for "home course" on the map: a home course is chosen on the
  // member's own page, and Explore only reads the set so it can filter by it.
  homeCourseIds: CourseIdSet;
  isLoading: boolean;
  // Both take the state the visitor asked for, not a "flip it" instruction: the checkbox already
  // knows which way it is going, and an explicit target survives a double click without the two
  // requests racing into the wrong end state.
  setPlayed: (courseId: string | undefined, played: boolean) => void;
  setBucketListed: (courseId: string | undefined, listed: boolean) => void;
  // Called after a sign-in on the same page. Nothing is replayed (D10 in
  // docs/EXPLORE.md); this only pulls in what the member already had saved.
  refresh: () => void;
}

export interface MyCourseFlagsOptions {
  // Called instead of the generic failure toast when a capped member's 31st played course is
  // refused. Explore answers it by opening UpgradeDrawer. The tick has already been rolled back by
  // the time this runs, so the handler only has to offer the upgrade.
  onFreeLimitReached?: (message: string) => void;
}

export const useMyCourseFlags = (
  options: MyCourseFlagsOptions = {},
): MyCourseFlags => {
  const { onFreeLimitReached } = options;
  const queryClient = useQueryClient();

  const [playedCourseIds, setPlayedCourseIds] = useState<CourseIdSet>(
    () => new Set(),
  );
  const [bucketListCourseIds, setBucketListCourseIds] = useState<CourseIdSet>(
    () => new Set(),
  );
  const [homeCourseIds, setHomeCourseIds] = useState<CourseIdSet>(
    () => new Set(),
  );
  const [isLoading, setIsLoading] = useState(false);

  // Whether this member is capped. Read from the membership status the backend already returns,
  // rather than from a count kept on that DTO: the played list is right here, so the count is a
  // .size away and does not need to travel.
  const [isFreeTier, setIsFreeTier] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    // Explore is a public page, so a signed-out visitor must not fire these — they are member-only
    // and would come back 401, which the axios interceptor turns into a bounce to /login.
    if (!isSignedIn()) {
      setPlayedCourseIds(new Set());
      setBucketListCourseIds(new Set());
      setHomeCourseIds(new Set());
      setIsFreeTier(false);
      return;
    }

    // Each list is applied the moment its own request lands. A Promise.all here made the played
    // count on the account flag wait for the slowest of all four, so it lagged the sign-in.
    // Failures are deliberately quiet: failing to load the ticks leaves the map fully usable, and
    // a toast on page load for something the visitor did not ask for is noise.
    const quietly = (error: unknown): void => {
      console.error('Could not load your saved courses:', error);
    };

    setIsLoading(true);
    await Promise.allSettled([
      coursesPlayedClient
        .getMyCoursesPlayed()
        .then((played) => setPlayedCourseIds(toCourseIdSet(played?.data)))
        .catch(quietly),
      bucketListClient
        .getMyBucketList()
        .then((bucketList) =>
          setBucketListCourseIds(toCourseIdSet(bucketList?.data)),
        )
        .catch(quietly),
      homeCourseClient
        .getMyHomeCourses()
        .then((homeCourses) =>
          setHomeCourseIds(toCourseIdSet(homeCourses?.data)),
        )
        .catch(quietly),
      memberClient
        .getMyMembershipStatus()
        .then((membership) =>
          setIsFreeTier(
            membership?.data != null &&
              isCoursesPlayedCapped(
                membership.data.memberTypeId,
                membership.data.active,
              ),
          ),
        )
        .catch(quietly),
    ]);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The member pages (/member/bucket-list, /member/courses-played, the dashboard) read the same
  // data through react-query with a 5 minute staleTime, so a tick here has to mark their cache
  // stale or they would show yesterday's list for five minutes.
  const invalidateMemberPages = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: ['myBucketList'] });
    void queryClient.invalidateQueries({ queryKey: ['myCoursesPlayed'] });
    // Not myHomeCourses: nothing on Explore writes a home course, so there is nothing to go stale.
  }, [queryClient]);

  const applyChange = useCallback(
    async (
      courseId: string | undefined,
      next: boolean,
      setIds: Dispatch<SetStateAction<CourseIdSet>>,
      call: (id: string) => Promise<unknown>,
      failureMessage: string,
    ): Promise<void> => {
      if (courseId == null) {
        return;
      }

      const key = courseId.toLowerCase();

      // Move the tick first and put it back if the save fails. The alternative — waiting for the
      // round trip — makes every tick feel broken on a slow connection.
      setIds((current) => withId(current, key, next));

      try {
        await call(courseId);
        invalidateMemberPages();
      } catch (error: unknown) {
        setIds((current) => withId(current, key, !next));

        // Running out of free courses is not a failure to report, it is an offer to make, so it
        // gets the upgrade drawer rather than a red toast saying something went wrong.
        const freeLimitMessage = freeLimitMessageFrom(error);
        if (freeLimitMessage != null && onFreeLimitReached != null) {
          onFreeLimitReached(freeLimitMessage);
          return;
        }

        showToast(failureMessage, 'error');

        console.error(failureMessage, error);
      }
    },
    [invalidateMemberPages, onFreeLimitReached],
  );

  const setPlayed = useCallback(
    (courseId: string | undefined, played: boolean): void => {
      // Ask the upgrade question before sending a request that can only be refused. The tick
      // never moves, so there is nothing to roll back and nothing flickers.
      //
      // Only a capped member is checked, only when adding, and only when the course is not already
      // on the list, because re-ticking a saved course is a no-op the server accepts. Un-ticking
      // frees a slot, so the count is the live size of the list, not a running total.
      //
      // This is a courtesy, not the gate. MemberCoursesPlayedService is what actually enforces the
      // limit, and the 403 handling below still stands on its own for a stale count or a tampered
      // browser.
      const key = courseId?.toLowerCase();
      if (
        played &&
        isFreeTier &&
        key != null &&
        !playedCourseIds.has(key) &&
        playedCourseIds.size >= FREE_COURSES_PLAYED_LIMIT
      ) {
        onFreeLimitReached?.(FREE_LIMIT_MESSAGE);
        return;
      }

      void applyChange(
        courseId,
        played,
        setPlayedCourseIds,
        (id) =>
          played
            ? coursesPlayedClient.addCourseToMyCoursesPlayed(id)
            : coursesPlayedClient.removeCourseFromMyCoursesPlayed(id),
        'Could not save that course as played.',
      );
    },
    [applyChange, isFreeTier, onFreeLimitReached, playedCourseIds],
  );

  const setBucketListed = useCallback(
    (courseId: string | undefined, listed: boolean): void => {
      void applyChange(
        courseId,
        listed,
        setBucketListCourseIds,
        (id) =>
          listed
            ? bucketListClient.addCourseToMyBucketList(id)
            : bucketListClient.removeCourseFromMyBucketList(id),
        'Could not save that bucket list change.',
      );
    },
    [applyChange],
  );

  const refresh = useCallback((): void => {
    void load();
  }, [load]);

  return {
    playedCourseIds,
    bucketListCourseIds,
    homeCourseIds,
    isLoading,
    setPlayed,
    setBucketListed,
    refresh,
  };
};
